import { differenceInCalendarDays, format } from "date-fns";

import type { ComplianceTaskInstanceResponseDto } from "@/generated/model/complianceTaskInstanceResponseDto";
import { ListTaskInstancesByNurseryQueryControllerListStatusItem as InstanceStatus } from "@/generated/model/listTaskInstancesByNurseryQueryControllerListStatusItem";

export type { SortDir } from "@/lib/category-display";

/** The four Compliance Manager tabs. Open is the default. */
export type ComplianceManagerTab =
  | "open"
  | "recently-completed"
  | "history"
  | "tasks";

/**
 * The tabs that list task instances. Tasks lists definitions instead, so it has
 * no instance sort or status configuration and no instance list query.
 */
export type ComplianceInstanceTab = Exclude<ComplianceManagerTab, "tasks">;

/** Sort columns supported server-side (must match the API allow-list). */
export type ComplianceSortColumn =
  | "task"
  | "category"
  | "responsible"
  | "dueDate"
  | "completed"
  | "status";

/** How far back the Recently completed tab reaches. */
export const RECENTLY_COMPLETED_WINDOW_DAYS = 7;

export const PAGE_SIZE = 10;

/**
 * Statuses that make up the Open tab. The server filters on the aggregate's
 * base status (which never yields DUE_SOON — that's a display overlay on
 * NOT_YET_DUE), but DUE_SOON is included defensively in case that changes.
 */
export const OPEN_STATUSES: InstanceStatus[] = [
  InstanceStatus.NOT_YET_DUE,
  InstanceStatus.DUE_SOON,
  InstanceStatus.OVERDUE,
  InstanceStatus.AWAITING_EVIDENCE,
];

export const COMPLETED_STATUSES: InstanceStatus[] = [InstanceStatus.COMPLETED];

/**
 * Statuses that make up the History tab: the full record excluding tasks that
 * are not yet due. Upcoming tasks (base status NOT_YET_DUE, which also covers
 * the DUE_SOON display overlay) belong in Open, not the history record.
 */
export const HISTORY_STATUSES: InstanceStatus[] = [
  InstanceStatus.OVERDUE,
  InstanceStatus.AWAITING_EVIDENCE,
  InstanceStatus.COMPLETED,
  InstanceStatus.CANCELLED,
];

/** Default sort per tab when the user hasn't picked a column. */
export const DEFAULT_SORT: Record<
  ComplianceInstanceTab,
  { column: ComplianceSortColumn; dir: "asc" | "desc" }
> = {
  open: { column: "dueDate", dir: "asc" },
  // The completion tabs don't show a due date column, so they default to the
  // completion date they do show. Rows with no completion sort last server-side.
  "recently-completed": { column: "completed", dir: "desc" },
  history: { column: "completed", dir: "desc" },
};

/** Columns each tab renders, and therefore the only ones it can sort by. */
export const SORTABLE_COLUMNS: Record<
  ComplianceInstanceTab,
  ComplianceSortColumn[]
> = {
  open: ["task", "category", "responsible", "dueDate", "status"],
  "recently-completed": [
    "task",
    "category",
    "responsible",
    "completed",
    "status",
  ],
  history: ["task", "category", "responsible", "completed", "status"],
};

/**
 * Relative label shown under the due date (e.g. "Overdue", "Due today",
 * "In 3 days"). Empty for terminal instances — a completed task's due date
 * needs no urgency cue.
 */
export function dueRelativeLabel(
  dueDate: string | Date,
  status: string,
  now: Date = new Date(),
): string {
  if (status === "COMPLETED" || status === "CANCELLED") return "";
  const days = differenceInCalendarDays(new Date(dueDate), now);
  if (days < 0) return "Overdue";
  if (days === 0) return "Due today";
  return days === 1 ? "In 1 day" : `In ${days} days`;
}

/** Table date cell: the formatted date, or an em dash when there isn't one. */
export function dateLabel(value?: string | Date | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return format(date, "d MMM yyyy");
}

/** Human-friendly label for a compliance definition frequency enum value. */
export function frequencyLabel(frequency?: string): string {
  switch (frequency) {
    case "WEEKLY":
      return "Weekly";
    case "MONTHLY":
      return "Monthly";
    case "SIX_MONTHLY":
      return "Every 6 months";
    case "YEARLY":
      return "Yearly";
    case "FIVE_YEARLY":
      return "Every 5 years";
    default:
      return "—";
  }
}

/**
 * Resolve the row's primary category from the catalog: the definition's
 * primaryCategoryId when set, else the first of its categoryIds (legacy
 * definitions predate primary categories).
 */
export function primaryCategory<
  T extends { id: string; name: string; slug?: string; color?: string | null },
>(
  instance: Pick<
    ComplianceTaskInstanceResponseDto,
    "primaryCategoryId" | "categoryIds"
  >,
  catalog: T[],
): T | undefined {
  const primaryId = instance.primaryCategoryId ?? instance.categoryIds?.[0];
  return primaryId ? catalog.find((c) => c.id === primaryId) : undefined;
}
